import { ListJobOrderFragment } from 'shared/hooks/api/listJobOrderFragment/listJobOrderFragment.generated';
import {
  activeApplicationProcessStatuses,
  contactedApplicationProcessStatuses,
  endedApplicationProcessStatuses,
} from 'types/enums/job-order-application-process-status.enum';
import { JobOrderListItem } from 'types/JobOrderListItem/JobOrderListItem';
import { mapDtoToJobOrderListItem } from 'types/JobOrderListItem/mappers';
import { create } from 'zustand';

const sortListByCreatedAt = (a: JobOrderListItem, b: JobOrderListItem) =>
  (a.application?.createdAt ?? a.createdAt) > (b.application?.createdAt ?? b.createdAt) ? -1 : 1;

export interface JobOrdersStateType {
  offers: JobOrderListItem[];
  activeApplied: JobOrderListItem[];
  endedApplied: JobOrderListItem[];
  observed: JobOrderListItem[];
  placements: JobOrderListItem[];
}

type JobOrderStoreListNames = (keyof JobOrdersStateType)[];

interface JobOrdersStoreType extends JobOrdersStateType {
  loadedLists: Record<keyof JobOrdersStateType, boolean>;
  loadListItems: (listName: keyof JobOrdersStateType, data: ListJobOrderFragment[]) => void;
  updateListItem: (id: string, payload: Partial<JobOrderListItem>) => void;
  moveListItem: (
    from: keyof JobOrdersStateType,
    to: keyof JobOrdersStateType,
    id: string,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) => void;
  getItemById: (id: string) => JobOrderListItem | null;
  copyListItem: (from: keyof JobOrdersStateType, to: keyof JobOrdersStateType, id: string) => void;
  pushListItem: (
    to: keyof JobOrdersStateType,
    item: JobOrderListItem,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) => void;
  removeListItem: (from: keyof JobOrdersStateType, id: string) => void;
  getListNamesById: (id: string) => JobOrderStoreListNames;
  getListNameById: (id: string) => keyof JobOrdersStateType | undefined;
  clearJobOrderLists: () => void;
}

const allListNames: JobOrderStoreListNames = [
  'offers',
  'observed',
  'activeApplied',
  'endedApplied',
  'placements',
];
const getListNamesById = (state: JobOrdersStoreType, id: string) => {
  return allListNames.filter((listName) => state[listName].some((item) => item.id === id));
};

const getListNameById = (state: JobOrdersStoreType, id: string) =>
  allListNames.find((listName) => state[listName].some((item) => item.id === id));

const getItemById = (state: JobOrdersStoreType, id: string) => {
  for (const listName of allListNames) {
    const item = state[listName].find((it) => it.id === id);
    if (item) {
      return item;
    }
  }
  return null;
};

const decideJobOrderLists = (jobOrder: JobOrderListItem): JobOrderStoreListNames => {
  const lists: JobOrderStoreListNames = [];
  if (jobOrder.application) {
    const { processStatus } = jobOrder.application;
    if (endedApplicationProcessStatuses.includes(processStatus)) {
      lists.push('endedApplied');
    }
    if (activeApplicationProcessStatuses.includes(processStatus)) {
      lists.push('activeApplied');
    }
    if (contactedApplicationProcessStatuses.includes(processStatus)) {
      lists.push('placements');
    }
    return lists;
  }

  if (jobOrder.isObserved) {
    lists.push('observed');
  }
  lists.push('offers');
  return lists;
};

const handleItemUpdate = (
  prev: JobOrdersStoreType,
  itemId: string,
  payload: Partial<JobOrderListItem>,
) => {
  const item = getItemById(prev, itemId);
  if (!item) {
    return {};
  }
  const updatedItem = { ...item, ...payload };

  const targetLists = decideJobOrderLists(updatedItem);
  const listsToRemove = getListNamesById(prev, item.id).filter(
    (listName) => !targetLists.includes(listName),
  );

  const listsWithItem = targetLists.reduce((acc, currListName) => {
    const isAlreadyOnList = prev[currListName].find(({ id }) => id === updatedItem.id);
    if (isAlreadyOnList) {
      acc[currListName] = prev[currListName].map((prevItem) =>
        prevItem.id === updatedItem.id ? updatedItem : prevItem,
      );
    } else {
      acc[currListName] = [updatedItem, ...prev[currListName]].sort(sortListByCreatedAt);
    }

    return acc;
  }, {} as Partial<JobOrdersStateType>);

  const listsWithRemovedItem = listsToRemove.reduce((acc, currListName) => {
    acc[currListName] = prev[currListName].filter(({ id }) => id !== updatedItem.id);
    return acc;
  }, {} as Partial<JobOrdersStateType>);

  return {
    ...listsWithItem,
    ...listsWithRemovedItem,
  };
};

const defaultLoadedLists = {
  offers: false,
  activeApplied: false,
  endedApplied: false,
  placements: false,
  observed: false,
};

export const useJobOrdersStore = create<JobOrdersStoreType>((set, get) => ({
  loadedLists: defaultLoadedLists,
  offers: [],
  observed: [],
  activeApplied: [],
  endedApplied: [],
  placements: [],
  getItemById: (jobOrderId: string) =>
    get().offers.find((jobOrder) => jobOrder.id === jobOrderId) ?? null,
  loadListItems: (listName: keyof JobOrdersStateType, data: ListJobOrderFragment[]) =>
    set((prev) => ({
      loadedLists: {
        ...prev.loadedLists,
        [listName]: true,
      },
      [listName]: data.map(mapDtoToJobOrderListItem),
    })),
  updateListItem: (id: string, payload: Partial<JobOrderListItem>) => {
    set((prev) => handleItemUpdate(prev, id, payload));
  },
  moveListItem: (
    from: keyof JobOrdersStateType,
    to: keyof JobOrdersStateType,
    id: string,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      let toList = [...prev[to], targetItem];
      if (mapFn) {
        toList = mapFn(toList);
      }

      return {
        [from]: prev[from].filter((item) => item.id !== id),
        [to]: toList,
      };
    }),
  copyListItem: (from: keyof JobOrdersStateType, to: keyof JobOrdersStateType, id: string) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      return {
        [to]: [targetItem, ...prev[to]],
      };
    }),
  removeListItem: (from: keyof JobOrdersStateType, id: string) =>
    set((prev) => {
      const targetItem = prev[from].find((item) => item.id === id);
      if (!targetItem) {
        return prev;
      }
      return {
        [from]: prev[from].filter((item) => item.id !== id),
      };
    }),
  getListNamesById: (id: string) => getListNamesById(get(), id),
  getListNameById: (id: string) => getListNameById(get(), id),
  pushListItem: (
    to: keyof JobOrdersStateType,
    item: JobOrderListItem,
    mapFn?: (arr: JobOrderListItem[]) => JobOrderListItem[],
  ) =>
    set((prev) => {
      let updated = [...(prev[to] ?? []), item];

      if (mapFn) {
        updated = mapFn(updated);
      }

      return {
        [to]: updated,
      };
    }),
  clearJobOrderLists: () =>
    set({
      loadedLists: defaultLoadedLists,
      offers: [],
      activeApplied: [],
      endedApplied: [],
      observed: [],
      placements: [],
    }),
}));
